import { DomainError } from '../errors.js';
import { effectiveResponse, LINE_RESPONSES, type LineResponse } from './rfq.js';

/**
 * Talebin zaman çizelgesi (04.11 notları D3/D6; sahip kararı 2026-11-05; ADR 0167 §2–§3): talep,
 * onay, iade, iptal, ofisin revizyonları (Rev. 1, Rev. 2 …) ve tedarikçi yanıtları tek listede.
 * Gemi ve ofis aynı biçimi görür; farkı veri belirler:
 * - Ofiste tedarikçi yanıtı tedarikçinin adı ve teklif tutarıyla gelir.
 * - Gemide tedarikçinin adı ve fiyatı yoktur: gemiye inen yanıt satırı kapalı listedir
 *   (`shipResponseLines`); yanıt "Tedarikçi 1, 2 …" sırasıyla, miktar ve durumla görünür.
 * - Tedarikçi bu geçmişi görmez (portal talebi okumaz).
 */

/** Tedarikçi yanıtının bir satırı (gemiye inen biçim; fiyat ve tedarikçi adı yok). */
export interface SupplierResponseLine {
  /** Talep satırı; RFQ'ya talepten gelmeyen satırda `null`. */
  requisitionLineId: string | null;
  lineNo: number;
  name: string;
  unit: string;
  response: LineResponse;
  /** Yanıtlanan miktar: RFQ satırınınki, ikamede ikamenin miktarı; reddedilen satırda `null`. */
  quantity: number | null;
  /** İkame ürünün adı ve birimi; ikame değilse `null`. */
  substituteName: string | null;
  substituteUnit: string | null;
}

/** Gemiye inen yanıt satırının anahtarları: bunun dışında hiçbir alan yazılmaz (ADR 0167 §3). */
export const SHIP_RESPONSE_LINE_KEYS = [
  'requisitionLineId',
  'lineNo',
  'name',
  'unit',
  'response',
  'quantity',
  'substituteName',
  'substituteUnit',
] as const satisfies readonly (keyof SupplierResponseLine)[];

/** Teklif satırından (fiyatlı biçim) yanıt satırına: kaynağın fazla alanları düşer. */
export interface QuotedLineSource {
  requisitionLineId: string | null;
  lineNo: number;
  name: string;
  unit: string;
  /** RFQ satırının miktarı. */
  quantity: number;
  response: LineResponse | null;
  unitPrice: number | null;
  substituteName?: string | null;
  substituteUnit?: string | null;
  substituteQuantity?: number | null;
}

/**
 * Gemiye inen yanıt satırları (ADR 0167 §3): kapalı liste. Fiyat, tedarikçi, not ve öteki her alan
 * düşer; sıra satır numarasıyla. Ofisin eski (yanıtsız) satırında yanıt fiyattan türer.
 */
export function shipResponseLines(lines: readonly QuotedLineSource[]): SupplierResponseLine[] {
  return [...lines]
    .sort((a, b) => a.lineNo - b.lineNo)
    .map((line) => {
      const response = effectiveResponse({
        response: line.response,
        unitPrice: line.unitPrice,
      });
      const substitute = response === 'substitute';
      return {
        requisitionLineId: line.requisitionLineId,
        lineNo: line.lineNo,
        name: line.name,
        unit: line.unit,
        response,
        quantity:
          response === 'declined'
            ? null
            : substitute
              ? (line.substituteQuantity ?? line.quantity)
              : line.quantity,
        substituteName: substitute ? (line.substituteName ?? null) : null,
        substituteUnit: substitute ? (line.substituteUnit ?? null) : null,
      };
    });
}

/**
 * Gemiye inecek yanıt satırlarını doğrular (yazma yolunun doğrulayıcısı): her satır yalnız kapalı
 * listedeki anahtarları taşır ve yanıt bilinen türdür. Fiyat ya da tedarikçi alanı taşıyan satır
 * `RESPONSE_LINE_NOT_ALLOWED` ile reddedilir: gemiye fiyat sızmaz.
 */
export function assertShipResponseLines(lines: unknown): asserts lines is SupplierResponseLine[] {
  if (!Array.isArray(lines) || lines.length === 0) {
    throw new DomainError('RESPONSE_LINES_REQUIRED');
  }
  const allowed = new Set<string>(SHIP_RESPONSE_LINE_KEYS);
  for (const line of lines) {
    if (line === null || typeof line !== 'object' || Array.isArray(line)) {
      throw new DomainError('RESPONSE_LINE_NOT_ALLOWED');
    }
    for (const key of Object.keys(line)) {
      if (!allowed.has(key)) throw new DomainError('RESPONSE_LINE_NOT_ALLOWED', key);
    }
    const value = line as Record<string, unknown>;
    if (!(LINE_RESPONSES as readonly unknown[]).includes(value.response)) {
      throw new DomainError('RESPONSE_LINE_NOT_ALLOWED', 'response');
    }
    if (typeof value.name !== 'string' || typeof value.lineNo !== 'number') {
      throw new DomainError('RESPONSE_LINE_NOT_ALLOWED', 'name');
    }
  }
}

/** Yanıtın satır özeti: kaç satır fiyatlı, fiyatsız, reddedilmiş, ikame. */
export function responseSummary(
  lines: readonly Pick<SupplierResponseLine, 'response'>[],
): Record<LineResponse, number> {
  const out: Record<LineResponse, number> = {
    priced: 0,
    no_price: 0,
    declined: 0,
    substitute: 0,
  };
  for (const line of lines) out[line.response] += 1;
  return out;
}

/** Talebin onay akışı olayı (gönderim, karar, iptal). */
export interface ApprovalHistoryEvent {
  kind: 'submitted' | 'approved' | 'returned' | 'cancelled';
  at: string;
  /** Talebin gönderim sırası (iadeden sonra yeniden gönderim 2, 3 …). */
  revision: number;
  step: number | null;
  byName: string;
  role: string | null;
  office: boolean;
  reason: string | null;
}

/** Ofisin talep revizyonu (ADR 0111 §A): numarası, gerekçesi ve değişiklik sayıları. */
export interface OfficeRevisionHistoryEvent {
  kind: 'office_revision';
  at: string;
  revisionNo: number;
  reason: string;
  /** Ofis kişisi gemiye inmez: gemide `null`. */
  byName: string | null;
  changed: number;
  removed: number;
  added: number;
}

/** Tedarikçinin teklifi ya da teklif revizyonu. */
export interface SupplierResponseHistoryEvent {
  kind: 'supplier_response';
  at: string;
  /** Talepte tedarikçinin sırası (ilk yanıt veren 1): gemide adın yerine. */
  responseNo: number;
  /** Tedarikçinin yanıt sürümü (1, 2 …). */
  revisionNo: number;
  source: 'portal' | 'office';
  /** Yalnız ofiste; gemide `null`. */
  supplierName: string | null;
  /** Yalnız ofiste: teklif edilen kalemlerin tutarı ve para birimi; gemide `null`. */
  amount: { value: number; currency: string } | null;
  lines: SupplierResponseLine[];
}

export type RequisitionHistoryEvent =
  | ApprovalHistoryEvent
  | OfficeRevisionHistoryEvent
  | SupplierResponseHistoryEvent;

const ORDER: Record<RequisitionHistoryEvent['kind'], number> = {
  submitted: 0,
  approved: 1,
  returned: 1,
  office_revision: 2,
  supplier_response: 3,
  cancelled: 4,
};

/**
 * Tek zaman çizelgesi: olaylar zamana göre; aynı anda olanlar akış sırasıyla (gönderim, karar,
 * ofis revizyonu, tedarikçi yanıtı, iptal), sonra numarayla. Girdi sırası korunur (kararlı).
 */
export function requisitionHistory(
  events: readonly RequisitionHistoryEvent[],
): RequisitionHistoryEvent[] {
  const number = (event: RequisitionHistoryEvent) =>
    event.kind === 'office_revision'
      ? event.revisionNo
      : event.kind === 'supplier_response'
        ? event.responseNo * 1_000 + event.revisionNo
        : 0;
  return events
    .map((event, index) => ({ event, index }))
    .sort(
      (a, b) =>
        Date.parse(a.event.at) - Date.parse(b.event.at) ||
        ORDER[a.event.kind] - ORDER[b.event.kind] ||
        number(a.event) - number(b.event) ||
        a.index - b.index,
    )
    .map(({ event }) => event);
}
